import { JSX, createResource } from "solid-js";
import { A, useParams } from "@solidjs/router";

import AsyncResource from "../components/AsyncResource";
import PageHeader from "../components/PageHeader";
import { api } from "../lib/api";
import { firmWords } from "../lib/orgWords";
import { useSession } from "../lib/session";

export default function UserDetail() {
  const session = useSession();
  const words = () => firmWords(session.admin());
  const params = useParams<{ userId: string }>();
  const [user] = createResource(
    () => params.userId,
    async (id) => (await api.getMember(id)).member,
  );
  const [challenges] = createResource(
    () => user()?.global_user_id,
    (globalId) => api.listChallenges({ global_user_id: globalId, limit: 25 }),
  );

  return (
    <>
      <AsyncResource resource={user}>
        {(u) => (
          <>
            <PageHeader
              title={u().email ?? u().display_name ?? u().global_user_id}
              subtitle={u().display_name ?? undefined}
              actions={
                <A class="btn-secondary" href="/users">
                  ← Back
                </A>
              }
            />

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <Field label="Global user ID" value={u().global_user_id} mono />
              <Field label="Role" value={u().role} />
              <Field
                label="Status"
                value={
                  <span
                    class={u().is_active ? "status-active" : "status-neutral"}
                  >
                    {u().is_active ? "active" : "disabled"}
                  </span>
                }
              />
              <Field
                label="Created"
                value={new Date(u().created_at).toLocaleString()}
              />
            </div>

            <div class="card">
              <div class="card-header">
                <h2 class="text-sm font-semibold">Recent {words()?.runs}</h2>
              </div>
              <AsyncResource resource={challenges}>
                {(page) => (
                  <>
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Instance</th>
                          <th>Status</th>
                          <th>P&amp;L</th>
                          <th>Started</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {page().challenges.map((c) => (
                          <tr>
                            <td class="font-mono text-xs">
                              {c.instance_id.slice(0, 8)}…
                            </td>
                            <td>
                              <span class={statusClass(c.status)}>
                                {c.status}
                              </span>
                            </td>
                            <td class="font-mono">{c.total_pnl ?? "—"}</td>
                            <td class="text-slate-500">
                              {new Date(c.started_at).toLocaleString()}
                            </td>
                            <td>
                              <A
                                class="text-sm underline"
                                href={`/challenges/${c.instance_id}`}
                              >
                                View
                              </A>
                            </td>
                          </tr>
                        ))}
                        {page().challenges.length === 0 && (
                          <tr>
                            <td colSpan={5} class="text-center text-slate-500 py-6">
                              No {words()?.runs} for this user.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </AsyncResource>
            </div>
          </>
        )}
      </AsyncResource>
    </>
  );
}

function Field(props: {
  label: string;
  value: JSX.Element;
  mono?: boolean;
}) {
  return (
    <div class="text-sm">
      <div class="text-xs text-slate-500">{props.label}</div>
      <div classList={{ "mt-1": true, "font-mono text-xs": !!props.mono }}>
        {props.value}
      </div>
    </div>
  );
}

function statusClass(status: string): string {
  switch (status) {
    case "active":
      return "status-active";
    case "passed":
      return "status-passed";
    case "failed":
      return "status-failed";
    case "pending_review":
      return "status-pending";
    case "suspended":
      return "status-suspended";
    default:
      return "status-neutral";
  }
}
